import { HttpClient } from '@adapters/HttpClient';
import {
  Order,
  PackagingApiResponse,
  PackagingDetail,
  PackingPayloadPackagingItem,
  Preparation,
  PreparationPickingOrdersResponse,
  PreparationWithProducts,
  Product,
} from '../models/Packing';

export interface ProductsApiResponse {
  data: Product[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

export interface PreparationsApiResponse {
  data: Preparation[];
  meta: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

export interface ConfirmPackingResponse {
  success: boolean;
  message: string;
  preparation?: Preparation;
}

export class PackingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  private getApiErrorMessage(error: any, fallback: string) {
    return error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || fallback;
  }

  /**
   * Mensaje legible para PUT change-packages (422 con errors.num_bultos, errores de dominio, etc.).
   * Tras el interceptor de ApiClient, `error.response` sigue siendo la respuesta Axios original.
   */
  private getChangePackagesErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    const numBultosMsgs = data?.errors?.num_bultos;
    if (Array.isArray(numBultosMsgs) && numBultosMsgs[0]) {
      return String(numBultosMsgs[0]);
    }
    if (data?.error?.message && typeof data.error.message === 'string') {
      return data.error.message;
    }
    if (typeof data?.error === 'string') {
      return data.error;
    }
    if (typeof error?.message === 'string' && error.message) {
      return error.message;
    }
    return fallback;
  }

  // Obtener preparaciones de tipo packing (tipo 3)
  async getPreparations(): Promise<Preparation[]> {
    try {
      const response = await this.httpClient.get<PreparationsApiResponse>('/preparations', {
        type: '3', // Tipo packing
        status: '1', // Solo activas
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });

      return response.data || [];
    } catch (error) {
      console.error('Error fetching preparations:', error);
      throw new Error('No se pudieron cargar las preparaciones');
    }
  }

  // Obtener una preparación específica por ID
  async getPreparation(id: number): Promise<Preparation> {
    try {
      const response = await this.httpClient.get<Preparation>(`/preparations/${id}`);

      // Validar que sea tipo packing
      if (response.type !== 3) {
        throw new Error('Esta preparación no es de tipo empaquetado');
      }

      return response;
    } catch (error) {
      console.error('Error fetching preparation:', error);
      throw new Error(`No se pudo cargar la preparación con ID ${id}`);
    }
  }

  // Obtener productos por IDs
  async getProductsByIds(productIds: number[]): Promise<Product[]> {
    try {
      if (productIds.length === 0) {
        return [];
      }

      const productIdsString = productIds.join(',');
      const response = await this.httpClient.get<ProductsApiResponse>('/products', {
        fields: 'basic',
        product_ids: productIdsString,
      });

      return response.data || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('No se pudieron cargar los productos');
    }
  }

  // Obtener pedidos de una preparación picking (líneas ya expandidas, sin packs)
  async getOrdersByPreparationId(preparationId: number): Promise<Order[]> {
    try {
      const response = await this.httpClient.get<PreparationPickingOrdersResponse>(
        `/preparations/picking/${preparationId}/orders`,
      );

      return response.data ?? [];
    } catch (error) {
      console.error('Error fetching preparation picking orders:', error);
      throw new Error(
        this.getApiErrorMessage(error, `No se pudieron cargar los pedidos de la preparación ${preparationId}`),
      );
    }
  }

  // Enviar datos de packing por pedido individual (cuando el pedido está completo)
  async sendOrderPackingData(
    preparationId: number,
    orderId: number,
    products: Array<{ product_id: number; quantity: number; batch_id?: number; location_id?: number }>,
    printerCode: string,
    packaging?: PackingPayloadPackagingItem[],
    attachmentPrinterCode?: string,
    packingStationId?: number,
  ): Promise<void> {
    try {
      const payload: Record<string, unknown> = {
        preparation_id: preparationId,
        order: orderId,
        printer_code: printerCode,
        products: products,
      };

      if (attachmentPrinterCode) {
        payload.attachment_printer_code = attachmentPrinterCode;
      }

      if (packingStationId != null) {
        payload.packing_station_id = packingStationId;
      }

      if (packaging && packaging.length > 0) {
        payload.packaging = packaging;
      }

      await this.httpClient.put(`/preparations/picking/${preparationId}/pack`, payload);
    } catch (error: any) {
      throw new Error(this.getApiErrorMessage(error, 'No se pudieron enviar los datos de packing del pedido'));
    }
  }

  // Obtener todos los packagings disponibles (paginado completo)
  async getAllPackagings(): Promise<PackagingDetail[]> {
    try {
      const items: PackagingDetail[] = [];
      let page = 1;
      const perPage = 100;
      let lastPage = 1;

      do {
        const response = await this.httpClient.get<PackagingApiResponse>('/packaging', {
          page,
          per_page: perPage,
        });

        items.push(...(response.data || []));
        lastPage = response.meta?.lastPage ?? 1;
        page += 1;
      } while (page <= lastPage);

      return items;
    } catch (error) {
      console.error('Error fetching packagings:', error);
      throw new Error('No se pudieron cargar los packagings');
    }
  }

  // Obtener preparación con productos completos
  async getPreparationWithProducts(id: number): Promise<PreparationWithProducts> {
    try {
      // Obtener directamente la preparación con productos incluidos
      const rawPreparation = await this.httpClient.get<PreparationWithProducts>(`/preparations/${id}`);
      // Mapear image_hash de cada línea
      const mappedPreparation: PreparationWithProducts = {
        ...rawPreparation,
        lines: rawPreparation.lines.map((line) => ({
          ...line,
          product: line.product
            ? {
                ...line.product,
                image_hash: line.product.image || line.product.image_hash,
              }
            : undefined,
        })),
      };
      return mappedPreparation;
    } catch (error) {
      console.error('Error fetching preparation with products:', error);
      throw error;
    }
  }

  // Confirmar empaquetado de un producto
  async confirmPacking(preparationId: number, lineId: number, quantity: number): Promise<ConfirmPackingResponse> {
    try {
      const response = await this.httpClient.put<ConfirmPackingResponse>(
        `/preparations/${preparationId}/lines/${lineId}/pack`,
        {
          quantity_to_add: quantity,
        },
      );

      return response;
    } catch (error: any) {
      console.error('Error confirming packing:', error);
      throw new Error(this.getApiErrorMessage(error, 'No se pudo confirmar el empaquetado'));
    }
  }

  // Obtener todas las preparaciones con productos
  async getAllPreparationsWithProducts(): Promise<PreparationWithProducts[]> {
    try {
      const preparations = await this.getPreparations();

      // Obtener preparaciones con productos en paralelo
      const preparationsWithProducts = await Promise.all(
        preparations.map(async (prep) => {
          try {
            return await this.getPreparationWithProducts(prep.id);
          } catch (error) {
            console.warn(`Error loading products for preparation ${prep.id}:`, error);
            // Retornar la preparación sin productos en caso de error
            return {
              ...prep,
              lines: [],
            };
          }
        }),
      );

      return preparationsWithProducts;
    } catch (error) {
      console.error('Error fetching all preparations with products:', error);
      throw new Error('No se pudieron cargar las preparaciones con productos');
    }
  }

  /**
   * Cambia el número de bultos del pedido (couriers que lo soportan; ventana y estado según backend).
   */
  async changeOrderPackages(orderId: number, numBultos: number): Promise<{ message: string }> {
    try {
      return await this.httpClient.put<{ message: string }>(`/orders/${orderId}/change-packages`, {
        num_bultos: numBultos,
      });
    } catch (error: any) {
      throw new Error(this.getChangePackagesErrorMessage(error, 'No se pudo cambiar el número de bultos.'));
    }
  }

  // 🎯 Imprimir etiqueta de pedido
  async printOrder(orderId: number, printingType: string = 'pdf', printerCode?: string): Promise<{ link: string }> {
    const params: any = {
      printing_type: printingType,
    };

    if (printerCode) {
      params.printer_code = printerCode;
    }

    return await this.httpClient.get(`/orders/${orderId}/print`, params);
  }
}
